'use client';

import {
  LeadCapturePage_gift,
  LeadCapturePage_offer,
} from '__generated__/graphql';
import { VehicleModel } from 'car-insurance/types';
import Script from 'next/script';
import React from 'react';
import { notEmpty } from 'utils/array';
import { Translated } from 'utils/staticText';
import {
  createProductBaseParameters,
  ITrackingProductPageBaseParameters,
} from '../lib/tracking';
import { Faq } from './Faq';
import { FaqStaticText } from './Faq/i18n';
import { RewardOffers } from './Offers';
import { OfferText } from './Offers/i18n';
import { Product } from './Product';
import { ProductText } from './Product/i18n';
import { ProductDialogStaticText } from './ProductDialog/i18n';
import { ProductHeader } from './ProductHeader';
import { ProductHeaderStaticText } from './ProductHeader/i18n';
import { RedirectPageStaticText } from './Redirect/i18n';
import { RewardCopyright } from './RewardCopyright';
import { RewardCopyrightText } from './RewardCopyright/i18n';
import { StickyQuickApply } from './StickyQuickApply';
import { StickyQuickApplyText } from './StickyQuickApply/i18n';
import { CatalogProductFragment } from './types';
import { ClippedTextStaticText } from '$/components/ClippedText/i18n';

export interface Provider {
  carrierCode: string;
  entryUid: string;
  providerImage: string;
  providerName: string;
  providerUid: string;
  urlAlias: string;
}

export interface ProductPageProps {
  clarityId?: string;
  logo: React.JSX.Element;
  lang: string;
  product: CatalogProductFragment;
  offer: LeadCapturePage_offer;
  gifts: LeadCapturePage_gift[];
  headerStaticText: Translated<typeof ProductHeaderStaticText>;
  productStaticText: Translated<typeof ProductText>;
  productDialogStaticText: Translated<typeof ProductDialogStaticText>;
  offerStaticText: Translated<typeof OfferText>;
  faqStaticText: Translated<typeof FaqStaticText>;
  faqLoaded: boolean;
  copyrightStaticText: Translated<typeof RewardCopyrightText>;
  stickyQuickApplyText: Translated<typeof StickyQuickApplyText>;
  clippedTextStaticText: Translated<typeof ClippedTextStaticText>;
  currencyDisplay: string;
  redirectStaticText: Translated<typeof RedirectPageStaticText>;
  providers?: Provider[];
  vehiclesData?: {
    makeNames: string[];
    models: VehicleModel[];
  };
}

export const ProductPage: React.FC<ProductPageProps> = ({
  clarityId,
  logo,
  lang,
  product,
  offer,
  gifts,
  headerStaticText,
  productStaticText,
  productDialogStaticText,
  offerStaticText,
  faqStaticText,
  faqLoaded,
  copyrightStaticText,
  stickyQuickApplyText,
  clippedTextStaticText,
  currencyDisplay,
  redirectStaticText,
  providers,
  vehiclesData,
}) => {
  const leadId = ''; // TODO: fill me
  const [marketingConsent, setMarketingConsent] = React.useState<boolean>(true);

  const tracking = React.useMemo<ITrackingProductPageBaseParameters>(
    () => ({
      ...createProductBaseParameters(product),
      marketingConsent,
      leadId,
      experiment_id: undefined,
      variant: undefined,
    }),
    [marketingConsent, product],
  );

  return (
    <div className="mhg-alpha-rewards-lead-capture-layout">
      <ProductHeader
        logo={logo}
        product={product}
        staticText={headerStaticText}
        tracking={tracking}
      />
      <div className="mhg-alpha-rewards-lead-capture-layout-content">
        <div className="mhg-alpha-rewards-lead-capture-frame">
          <Product
            product={product}
            attributes={product?.attributeList}
            loanCalculation={product?.calculatePersonalLoan}
            staticText={productStaticText}
            productDialogStaticText={productDialogStaticText}
            variant="VARIANT_A"
            tracking={tracking}
          />
          {notEmpty(gifts) && (
            <RewardOffers
              offer={offer}
              gifts={gifts}
              product={product}
              attributes={product?.attributeList}
              staticText={offerStaticText}
              productDialogStaticText={productDialogStaticText}
              tracking={tracking}
              currencyDisplay={currencyDisplay}
            />
          )}
          {faqLoaded && <Faq staticText={faqStaticText} tracking={tracking} />}
          <RewardCopyright staticText={copyrightStaticText} />
          <StickyQuickApply
            lang={lang}
            product={product}
            offer={offer}
            staticText={stickyQuickApplyText}
            marketingConsent={marketingConsent}
            onChangeMarketingConsent={setMarketingConsent}
            tracking={tracking}
            clippedTextStaticText={clippedTextStaticText}
            redirectStaticText={redirectStaticText}
            currencyDisplay={currencyDisplay}
            attributes={product?.attributeList}
            providers={providers}
            vehiclesData={vehiclesData}
          />
        </div>
      </div>
      {clarityId && (
        <Script
          id="clarity-script"
          dangerouslySetInnerHTML={{
            __html: `
            (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "${clarityId}");
        `,
          }}
          strategy="lazyOnload"
        />
      )}
    </div>
  );
};
